// NewVisual.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Wrapper from '../../components/Wrapper'
import Utils from '../../Utils'
import {
  Controls,
  MiniMap,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import Paper from '@mui/material/Paper'
import { Box, CircularProgress, Typography } from '@mui/material'
import Api from '../../Api'
import CustomNode from '../../components/CustomNode'
import Select from 'react-select'
import { dragConnect, dragConnectTypes, shortFunctionList } from '../../translate'
import { greenTheme } from '../../helpers/style'

const nodeTypes = { custom: CustomNode }

let sourceColorMap = { '55': 'blue', '2': 'green', '3': 'red' }
const getColorForSource = (source) => sourceColorMap[source] || 'gray'
const getSubLabel = (item) =>
  (item?.notes ?? item?.name ?? item?.context ?? item?.title ?? '').toString().trim()

const customStyles = {
  menu: (provided) => ({ ...provided, position: 'absolute', zIndex: 9999, maxHeight: 300 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (provided, state) => ({
    ...provided,
    minHeight: 44,
    borderRadius: 10,
    borderColor: state.isFocused ? '#5b7cff' : '#d9dce3',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(91,124,255,0.15)' : 'none',
    '&:hover': { borderColor: '#5b7cff' },
    backgroundColor: '#fff',
  }),
  valueContainer: (provided) => ({ ...provided, padding: '0 10px', fontSize: 14 }),
  placeholder: (provided) => ({ ...provided, color: '#94a3b8', fontSize: 14 }),
  singleValue: (provided) => ({ ...provided, color: '#1f2937', fontSize: 14 }),
}

// centered row: if count=1 => centerX, else spread symmetrically
const rowX = (centerX, index, count, gap) => centerX + (index - (count - 1) / 2) * gap
const toDragSingleType = (rawType) => ((rawType || '').toLowerCase() === 'sip' ? 'sip' : 'group')

export default function NewVisual() {
  const getLang = () => +localStorage.getItem('atsLang') || 1
  const getShortFunctionLabel = (number) => {
    const key = String(number || '').trim()
    if (!key) return ''
    return shortFunctionList?.[key]?.[getLang()] || ''
  }

  const formatTypeLabel = (raw) => {
    const key = (raw || '').toUpperCase()
    const groupFallback = ['', 'Group / Subdivision', 'Группа / Подразделения', 'Խումբ / Ստորաբաժանում']
    const didFallback = ['', 'DID number', 'DID номер', 'Գլխավոր համար']
    if (key === 'GROUP') return groupFallback[getLang()]
    if (key === 'DID') return didFallback[getLang()]
    if (dragConnectTypes[key]) return dragConnectTypes[key][getLang()]
    return key || ''
  }
  // When item has isivr === 1, treat as ivr (Voice menu), not group
  const getEffectiveType = (item) => (item?.isivr === 1 ? 'ivr' : (item?.type || 'group'))

  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [type, setType] = useState('did')
  const [selectedNumber, setSelectedNumber] = useState('')
  const [nums, setNums] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [initializing, setInitializing] = useState(true)

  const calculateDepth = async (itemId, itemType, currentDepth = 0, visited = new Set()) => {
    const key = `${itemId}_${itemType}`
    if (visited.has(key)) return currentDepth
    visited.add(key)

    try {
      const response = await Api.dragSingle(itemId, toDragSingleType(itemType))
      if (response?.data?.data?.length) {
        const items = response.data.data || []
        const uniqueData = Array.from(
          new Set(items.map((s) => `${s.id}_${s.buddy}`))
        ).map((k) => {
          const [id, buddy] = k.split('_')
          return { id, buddy }
        })

        let maxChildDepth = currentDepth
        for (const child of uniqueData) {
          const childType = response.data.label ? response.data.label.toLowerCase() : 'sip'
          const childDepth = await calculateDepth(child.id, childType, currentDepth + 1, visited)
          maxChildDepth = Math.max(maxChildDepth, childDepth)
        }
        return maxChildDepth
      }
    } catch (e) {}
    return currentDepth
  }

  // Returns { depth, count } so we can prefer both deepest and most items (e.g. "more items in short")
  const calculateDepthAndCount = async (itemId, itemType, currentDepth = 0, visited = new Set()) => {
    const key = `${itemId}_${itemType}`
    if (visited.has(key)) return { depth: currentDepth, count: 0 }
    visited.add(key)

    try {
      const response = await Api.dragSingle(itemId, toDragSingleType(itemType))
      if (response?.data?.data?.length) {
        const items = response.data.data || []
        const uniqueData = Array.from(
          new Set(items.map((s) => `${s.id}_${s.buddy}`))
        ).map((k) => {
          const [id, buddy] = k.split('_')
          return { id, buddy }
        })

        let maxChildDepth = currentDepth
        let totalCount = 1 // this node
        for (const child of uniqueData) {
          const childType = response.data.label ? response.data.label.toLowerCase() : 'sip'
          const childResult = await calculateDepthAndCount(child.id, childType, currentDepth + 1, visited)
          maxChildDepth = Math.max(maxChildDepth, childResult.depth)
          totalCount += childResult.count
        }
        return { depth: maxChildDepth, count: totalCount }
      }
    } catch (e) {}
    return { depth: currentDepth, count: 1 }
  }

  const isBetter = (best, candidate) => {
    if (!best.depth && !best.count) return true
    if (candidate.depth > best.depth) return true
    if (candidate.depth === best.depth && candidate.count > best.count) return true
    return false
  }

  useEffect(() => {
    if (initialized) return

    ;(async () => {
      setInitializing(true)
      try {
        const { data } = await Api.drag()
        const allData = data.data

        let best = { type: '', number: '', depth: 0, count: 0 }

        if (allData.did) {
          for (const didItem of allData.did) {
            if (!didItem.child) continue
            let depth = 1
            let count = 1 // DID node
            try {
              if (didItem.child.id && getEffectiveType(didItem.child)) {
                const childResult = await calculateDepthAndCount(
                  didItem.child.id,
                  getEffectiveType(didItem.child).toLowerCase()
                )
                depth = Math.max(depth, childResult.depth + 1)
                count += childResult.count // child + its subtree
              }
            } catch (e) {}
            const candidate = { type: 'did', number: `${didItem.pref}${didItem.did}`, depth, count }
            if (isBetter(best, candidate)) best = candidate
          }
        }

        const typeChecks = [
          { type: 'sip', dataKey: 'sip', numberKey: 'buddy' },
          { type: 'short', dataKey: 'shortAll', numberKey: 'virtual' },
          { type: 'ivr', dataKey: 'ivr', numberKey: 'virtual' },
        ]

        for (const check of typeChecks) {
          if (!allData[check.dataKey]) continue
          for (const item of allData[check.dataKey]) {
            if (check.filter && !check.filter(item)) continue
            let depth = 0
            let count = 1
            try {
              if (item.id) {
                const result = await calculateDepthAndCount(item.id, check.type)
                depth = result.depth
                count = result.count
              }
            } catch (e) {}
            const candidate = {
              type: check.type,
              number: item[check.numberKey]?.toString() || '',
              depth,
              count,
            }
            if (isBetter(best, candidate)) best = candidate
          }
        }

        const bestItem = { type: best.type, number: best.number }

        if (bestItem.type && bestItem.number) {
          setType(bestItem.type)
          setInitialized(true)

          try {
            const { data: typeData } = await Api.type(bestItem.type)
            const s = typeData.data.map((d) => ({
              value: d.number,
              label: d.name ? `${d.number}\n${d.name}` : `${d.number}`,
            }))
            setNums(s)

            setTimeout(() => {
              setSelectedNumber(bestItem.number)
              setInitializing(false)
            }, 100)
          } catch (e) {
            setInitialized(true)
            setInitializing(false)
          }
        } else {
          setInitialized(true)
          setInitializing(false)
        }
      } catch (e) {
        setInitialized(true)
        setInitializing(false)
      }
    })()
  }, [initialized])

  useEffect(() => {
    if (!type) {
      setNums([])
      setSelectedNumber('')
      return
    }

    if (type === 'included') {
      setType('')
      setNums([])
      setSelectedNumber('')
      return
    }

    ;(async () => {
      try {
        const { data } = await Api.type(type)
        const s = data.data.map((d) => ({
          value: d.number,
          label: d.name ? `${d.number}\n${d.name}` : `${d.number}`,
        }))
        setNums(s)
      } catch (e) {}
    })()
  }, [type])

  useEffect(() => {
    if (!type || !selectedNumber || type === 'included') {
      setNodes([])
      setEdges([])
      return
    }

    setEdges([])
    setNodes([])

    ;(async () => {
      setLoading(true)
      try {
        const { data } = await Api.drag()
        const allData = data.data

        let allNodes = []
        let allEdges = []

        const sourceY = 40
        const targetY = 320

        // ✅ IMPORTANT: use flow-coordinate center (0), not window width
        const centerX = 0
        const currentX = 0

        const gap = 220 // spacing between siblings when multiple

        if (type === 'did') {
          const selectedItem = allData.did?.find((s) => {
            const didNumber = `${s.pref}${s.did}`
            return didNumber === selectedNumber || `${s.pref}${s.did} ${s.notes}` === selectedNumber
          })

          if (selectedItem && selectedItem.child) {
            const color = Utils.getRandomColor()
            const didId =
              selectedItem.pref?.toString() +
              selectedItem.did?.toString() +
              selectedItem?.provider?.toString()

            sourceColorMap = { ...sourceColorMap, [didId]: color }

            const didNode = {
              id: didId,
              type: 'custom',
              position: { x: currentX, y: sourceY },
              data: {
                color,
                label: `${selectedItem.pref}${selectedItem.did}`,
                subLabel: getSubLabel(selectedItem),
                type: 'DID',
                displayType: formatTypeLabel('DID'),
                uid: didId,
                lid: didId,
                setNodes,
                setEdges,
              },
            }

            const didRedirectOutput = selectedItem?.redirect?.output
            const childNumberRaw =
              selectedItem?.child?.virtual ??
              selectedItem?.child?.buddy ??
              didRedirectOutput ??
              selectedItem?.child?.id
            const childNumber = childNumberRaw != null ? String(childNumberRaw) : ''
            const shortFnLabel = getShortFunctionLabel(childNumber)
            const childLooksLikeEmptySip =
              getEffectiveType(selectedItem?.child)?.toLowerCase() === 'sip' &&
              !selectedItem?.child?.buddy &&
              !selectedItem?.child?.id &&
              !!shortFnLabel

            const childId = `${childNumber}_${didId}`
            const childIsFunctionTarget =
              (!!selectedItem?.child?.virtual && getEffectiveType(selectedItem?.child)?.toLowerCase() !== 'sip') ||
              childLooksLikeEmptySip
            const childName = getSubLabel(selectedItem?.child) || shortFnLabel
            const childLabel = childIsFunctionTarget
              ? childName || childNumber || '—'
              : childNumber || childName || '—'
            const childSubLabel = childIsFunctionTarget ? '' : getSubLabel(selectedItem?.child)
            const childDisplayType = childLooksLikeEmptySip
              ? 'GROUP'
              : (getEffectiveType(selectedItem?.child)?.toUpperCase() || 'SIP')
            const childDisplayTypeLabel = childLooksLikeEmptySip
              ? formatTypeLabel('GROUP')
              : formatTypeLabel(getEffectiveType(selectedItem?.child))

            const childNode = {
              id: childId,
              type: 'custom',
              position: { x: currentX, y: targetY },
              data: {
                color,
                uid: selectedItem?.child?.id,
                lid: childId,
                child_type: 'SIP',
                position: { x: currentX, y: targetY },
                label: childLabel,
                subLabel: childSubLabel,
                type: childDisplayType,
                displayType: childDisplayTypeLabel,
                hideTypeLabel: childLooksLikeEmptySip,
                noaction: selectedItem?.child?.noaction,
                setNodes,
                setEdges,
              },
            }

            allNodes = [didNode, childNode]

            allEdges = [
              {
                id: didId,
                source: didId,
                sourceHandle: `DID_source_${didId}`,
                target: childId,
                targetHandle: `${childDisplayType}_target_${childId}`,
                type: 'straight',
                animated: true,
                style: { stroke: color, strokeWidth: 2, opacity: 0, transition: 'opacity 0.5s ease-in' },
              },
            ]

            if (selectedItem?.child?.id && getEffectiveType(selectedItem?.child)) {
              const childTypeForApi = getEffectiveType(selectedItem.child)
              const childResponse = await Api.dragSingle(
                selectedItem.child.id,
                childTypeForApi === 'sip' ? 'sip' : 'group'
              )

              if (childResponse?.data?.data?.length) {
                const childRawItems = childResponse.data.data || []
                const seen = new Set()
                const uniqueData = childRawItems.filter((s) => {
                  const k = `${s.id}_${s.buddy}`
                  if (seen.has(k)) return false
                  seen.add(k)
                  return true
                })

                const childCount = uniqueData.length
                const ivrNoAction = selectedItem?.child?.noaction?.toString()
                const showNoActionShort =
                  getEffectiveType(selectedItem?.child) === 'ivr' && !!ivrNoAction && childCount > 0
                const shortNodeId = showNoActionShort ? `${ivrNoAction}_${childId}` : null
                const shortNodeY = targetY + 200

                if (showNoActionShort) {
                  const shortName =
                    allData?.shortAll?.find((x) => `${x.virtual}` === ivrNoAction)?.name ||
                    getShortFunctionLabel(ivrNoAction)
                  allNodes.push({
                    id: shortNodeId,
                    type: 'custom',
                    position: { x: centerX, y: shortNodeY },
                    data: {
                      color,
                      label: ivrNoAction,
                      subLabel: getSubLabel({ name: shortName }),
                      type: 'GROUP',
                      displayType: formatTypeLabel('GROUP'),
                      uid: ivrNoAction,
                      lid: shortNodeId,
                      child_type: 'GROUP',
                      position: { x: centerX, y: shortNodeY },
                      setNodes,
                      setEdges,
                    },
                  })

                  allEdges.push({
                    id: `${shortNodeId}_from_${childId}`,
                    source: childId,
                    sourceHandle: `${getEffectiveType(selectedItem?.child)?.toUpperCase()}_source_${childId}`,
                    target: shortNodeId,
                    targetHandle: `GROUP_target_${shortNodeId}`,
                    type: 'straight',
                    animated: true,
                    style: { stroke: color, strokeWidth: 2, opacity: 0, transition: 'opacity 0.5s ease-in' },
                  })
                }

                const childNodes = uniqueData.map((s, i) => {
                  const newId = `${s.id}_${s.buddy}`
                  const childType = childResponse.data.label ? childResponse.data.label.toUpperCase() : 'SIP'

                  // ✅ if one child => x = 0, else spread side-by-side centered
                  const posX = rowX(centerX, i, childCount, gap)
                  const posY = showNoActionShort ? targetY + 400 : targetY + 200

                  allEdges.push({
                    id: `${s.id}_${childId}`,
                    source: showNoActionShort ? shortNodeId : childId,
                    sourceHandle: showNoActionShort
                      ? `GROUP_source_${shortNodeId}`
                      : `${getEffectiveType(selectedItem?.child)?.toUpperCase()}_source_${childId}`,
                    target: newId,
                    targetHandle: `${childType}_target_${newId}`,
                    type: 'straight',
                    animated: true,
                    style: { stroke: color, strokeWidth: 2, opacity: 0, transition: 'opacity 0.5s ease-in' },
                  })

                  return {
                    id: newId,
                    type: 'custom',
                    position: { x: posX, y: posY },
                    data: {
                      color,
                      label: `${s.buddy}`,
                      subLabel: getSubLabel(s),
                      type: childType,
                      displayType: formatTypeLabel(childType),
                      uid: s.buddy,
                      lid: newId,
                      child_type: childType,
                      noaction: s?.noaction,
                      position: { x: posX, y: posY },
                      setNodes,
                      setEdges,
                    },
                  }
                })

                allNodes = [...allNodes, ...childNodes]
              }
            }
          }
        } else {
          let foundItem = null
          const typeMap = { sip: 'sip', short: 'shortAll', ivr: 'ivr' }
          const dataKey = typeMap[type]

          if (dataKey && allData[dataKey]) {
            if (type === 'sip') foundItem = allData[dataKey]?.find((s) => s.buddy === selectedNumber)
            if (type === 'short' || type === 'ivr') foundItem = allData[dataKey]?.find((s) => s.virtual === selectedNumber)
          }

          if (foundItem) {
            const color = Utils.getRandomColor()
            const nodeId = foundItem.id?.toString() || selectedNumber
            sourceColorMap = { ...sourceColorMap, [nodeId]: color }

            const line1 = type === 'sip' ? foundItem.buddy : foundItem.virtual
            const line2 = getSubLabel(foundItem)

            const nodeType = type === 'ivr' ? 'IVR' : type === 'short' ? 'GROUP' : type.toUpperCase()

            const mainNode = {
              id: nodeId,
              type: 'custom',
              position: { x: currentX, y: sourceY },
              data: {
                color,
                label: line1,
                subLabel: line2,
                type: nodeType,
                displayType: formatTypeLabel(type),
                uid: foundItem.id || selectedNumber,
                lid: nodeId,
                child_type: nodeType,
                noaction: foundItem?.noaction,
                position: { x: currentX, y: sourceY },
                setNodes,
                setEdges,
              },
            }

            allNodes = [mainNode]

            const response = await Api.dragSingle(foundItem.id, toDragSingleType(type))
            if (response?.data?.data?.length) {
              const rawItems = response.data.data || []
              const seen = new Set()
              const uniqueData = rawItems.filter((s) => {
                const k = `${s.id}_${s.buddy}`
                if (seen.has(k)) return false
                seen.add(k)
                return true
              })

              const childCount = uniqueData.length
              const ivrNoAction = foundItem?.noaction?.toString()
              const showNoActionShort = type === 'ivr' && !!ivrNoAction && childCount > 0
              const shortNodeId = showNoActionShort ? `${ivrNoAction}_${nodeId}` : null

              if (showNoActionShort) {
                const shortName =
                  allData?.shortAll?.find((x) => `${x.virtual}` === ivrNoAction)?.name ||
                  getShortFunctionLabel(ivrNoAction)
                allNodes.push({
                  id: shortNodeId,
                  type: 'custom',
                  position: { x: centerX, y: sourceY + 200 },
                  data: {
                    color,
                    label: ivrNoAction,
                    subLabel: getSubLabel({ name: shortName }),
                    type: 'GROUP',
                    displayType: formatTypeLabel('GROUP'),
                    uid: ivrNoAction,
                    lid: shortNodeId,
                    child_type: 'GROUP',
                    position: { x: centerX, y: sourceY + 200 },
                    setNodes,
                    setEdges,
                  },
                })

                allEdges.push({
                  id: `${shortNodeId}_from_${nodeId}`,
                  source: nodeId,
                  sourceHandle: `${nodeType}_source_${nodeId}`,
                  target: shortNodeId,
                  targetHandle: `GROUP_target_${shortNodeId}`,
                  type: 'straight',
                  animated: true,
                  style: { stroke: color, strokeWidth: 2, opacity: 0, transition: 'opacity 0.5s ease-in' },
                })
              }

              const childNodes = uniqueData.map((s, i) => {
                const newId = `${s.id}_${s.buddy}`
                const childType = response.data.label ? response.data.label.toUpperCase() : 'SIP'

                // ✅ if one child => x = 0, else spread side-by-side centered
                const posX = rowX(centerX, i, childCount, gap)
                const posY = showNoActionShort ? sourceY + 400 : sourceY + 200

                allEdges.push({
                  id: `${s.id}_${nodeId}`,
                  source: showNoActionShort ? shortNodeId : nodeId,
                  sourceHandle: showNoActionShort ? `GROUP_source_${shortNodeId}` : `${nodeType}_source_${nodeId}`,
                  target: newId,
                  targetHandle: `${childType}_target_${newId}`,
                  type: 'straight',
                  animated: true,
                  style: { stroke: color, strokeWidth: 2, opacity: 0, transition: 'opacity 0.5s ease-in' },
                })

                return {
                  id: newId,
                  type: 'custom',
                  position: { x: posX, y: posY },
                  data: {
                    color,
                    label: `${s.buddy}`,
                    subLabel: getSubLabel(s),
                    type: childType,
                    displayType: formatTypeLabel(childType),
                    uid: s.buddy,
                    lid: newId,
                    child_type: childType,
                    noaction: s?.noaction,
                    position: { x: posX, y: posY },
                    setNodes,
                    setEdges,
                  },
                }
              })

              allNodes = [...allNodes, ...childNodes]
            }
          }
        }

        setNodes(allNodes)
        setEdges(allEdges)

        setTimeout(() => {
          setEdges((prev) => prev.map((e) => ({ ...e, type: 'straight', animated: true, style: { ...e.style, opacity: 1 } })))
        }, 100)
      } catch (e) {
        setNodes([])
        setEdges([])
      } finally {
        setLoading(false)
      }
    })()
  }, [type, selectedNumber, setNodes, setEdges])

  const handleNumberChange = useCallback((selectedOption) => {
    setSelectedNumber(selectedOption?.value || '')
  }, [])

  const visibleNodes = useMemo(() => {
    const isIncluded = (n) => {
      const label = String(n?.data?.label || '').trim().toLowerCase()
      const t = String(n?.data?.type || '').trim().toUpperCase()
      return label === 'included' || t === 'INCLUDED'
    }
    return nodes.filter((n) => n?.data?.label && n.data.label !== 'undefined' && !isIncluded(n))
  }, [nodes])

  const visibleNodeIds = useMemo(() => new Set(visibleNodes.map((n) => n.id)), [visibleNodes])

  const straightEdges = useMemo(() => {
    return edges
      .map((e) => ({
        ...e,
        type: 'straight',
        animated: true,
        style: { ...e.style, stroke: e.style?.stroke || getColorForSource(e.source) },
      }))
      .filter((e) => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target))
  }, [edges, visibleNodeIds])

  const formatOptionLabel = (opt) => {
    const parts = String(opt.label || '').split('\n')
    return (
      <div style={{ lineHeight: 1.15 }}>
        <div style={{ fontWeight: 700 }}>{parts[0]}</div>
        {parts[1] ? <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{parts[1]}</div> : null}
      </div>
    )
  }

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper
          sx={{
            minWidth: 'fit-content',
            fontSize: 10,
            borderRadius: '14px',
            overflow: 'hidden',
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.09)',
          }}
        >
          <div style={{ padding: '18px 24px', borderBottom: '1px solid #e6e8ee' }}>
            <h1 className="m-0" style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
              {dragConnect.controllers?.[getLang()] || 'Visualization'}
            </h1>
          </div>

          <div className="control" style={{ padding: '20px 24px 18px', borderBottom: '1px solid #e6e8ee' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  {dragConnect.chooseType?.[getLang()] || 'Choose Type'}
                </span>
                <select
                  value={type}
                  disabled={true}
                  onChange={(ev) => {
                    const v = ev.target.value
                    if (v === 'included') return
                    setType(v)
                    setSelectedNumber('')
                  }}
                  style={{
                    width: '100%',
                    padding: '11px 12px',
                    borderRadius: 10,
                    border: '1px solid #d9dce3',
                    background: '#fff',
                    fontSize: 14,
                    color: '#111827',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                    outline: 'none',
                  }}
                >
                  <option value="">{dragConnect.select?.[getLang()] || 'Select'}</option>
                  <option value="did">{dragConnect.didNumber?.[getLang()] || 'DID Number'}</option>
                  <option value="sip">{dragConnect.sipNumber?.[getLang()] || 'SIP Number'}</option>
                  <option value="short">{dragConnect.shortNumber?.[getLang()] || 'Short Number'}</option>
                  <option value="ivr">{dragConnect.ivr?.[getLang()] || 'IVR'}</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  {dragConnect.chooseElement?.[getLang()] || 'Choose Element'}
                </span>
                <Select
                  styles={customStyles}
                  menuPlacement="auto"
                  menuPosition="fixed"
                  onChange={handleNumberChange}
                  menuPortalTarget={document.body}
                  placeholder={dragConnect.chooseElement?.[getLang()] || dragConnect.select?.[getLang()] || 'Select'}
                  className="basic-single"
                  classNamePrefix="select"
                  isSearchable={true}
                  isDisabled={!type}
                  value={selectedNumber ? { value: selectedNumber, label: selectedNumber } : null}
                  name="number"
                  options={[{ value: '', label: dragConnect.select?.[getLang()] || 'Select' }, ...nums]}
                  isLoading={loading}
                  formatOptionLabel={formatOptionLabel}
                />
              </div>
            </div>
          </div>

          <div style={{ width: '100%', height: 'calc(100vh - 200px)', position: 'relative' }}>
            {initializing ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  zIndex: 1000,
                }}
              >
                <CircularProgress sx={{ color: greenTheme.primary }} />
                <Typography variant="body2" color="text.secondary">
                  {dragConnect.findingDeepestStructure[getLang()]}
                </Typography>
              </Box>
            ) : (
              <ReactFlow
                nodes={visibleNodes}
                edges={straightEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={true}
                fitView
                nodeOrigin={[0.5, 0.5]}
                fitViewOptions={{ padding: 0.25 }}
              >
                <Controls />
                <MiniMap />
              </ReactFlow>
            )}
          </div>
        </Paper>
      </div>
    </Wrapper>
  )
}
